'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import SiteHeader from '@/app/error-explainer-tool/components/SiteHeader';

const LANGUAGES = [
  { value: '', label: 'Auto-detect' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'php', label: 'PHP' },
  { value: 'java', label: 'Java' },
  { value: 'sql', label: 'SQL' },
  { value: 'rust', label: 'Rust' },
  { value: 'go', label: 'Go' },
  { value: 'csharp', label: 'C#' },
  { value: 'cpp', label: 'C++' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'other', label: 'Other' },
];

interface RelatedError {
  id: string;
  error_message: string;
  language: string;
  slug: string;
  url: string;
}

interface DebugResult {
  explanation: string;
  cause: string;
  fix: string;
  example_code: string;
  related_errors: RelatedError[];
  matched: boolean;
  similarity_score: number;
}

const EXAMPLE_ERRORS = [
  { label: 'JS TypeError', value: 'TypeError: Cannot read properties of undefined (reading \'map\')', lang: 'javascript' },
  { label: 'Python IndexError', value: 'IndexError: list index out of range', lang: 'python' },
  { label: 'PHP Undefined Index', value: 'Notice: Undefined index: user_id in /var/www/html/index.php on line 42', lang: 'php' },
  { label: 'SQL Syntax Error', value: "You have an error in your SQL syntax; check the manual that corresponds to your MySQL server version", lang: 'sql' },
];

export default function DebugPage() {
  const [errorMessage, setErrorMessage] = useState('');
  const [language, setLanguage] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<DebugResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  async function handleAnalyze() {
    if (!errorMessage.trim()) return;
    setIsAnalyzing(true);
    setResult(null);
    setError(null);

    try {
      const res = await fetch('/api/debug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: errorMessage.trim(),
          language: language || undefined,
          source: 'human',
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setResult(data as DebugResult);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  }

  function handleExampleClick(ex: typeof EXAMPLE_ERRORS[0]) {
    setErrorMessage(ex.value);
    setLanguage(ex.lang);
    setResult(null);
    setError(null);
  }

  function handleCopyCode() {
    if (result?.example_code) {
      navigator.clipboard.writeText(result.example_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <SiteHeader />

      <div className="border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <Link
            href="/error-explainer-tool"
            className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors text-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Explainer
          </Link>

          <a
            href="/api/debug"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 text-xs px-3 py-1.5 rounded-lg border border-cyan-500/30 hover:border-cyan-500/60 bg-cyan-500/5 transition-all"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
            API Docs
          </a>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* Hero */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 text-xs text-cyan-400 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            AI-Powered Debugging Engine
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Paste Your Error. Get the Fix.
          </h1>
          <p className="text-zinc-400 text-base max-w-xl mx-auto">
            Instantly analyze any programming error. Our AI searches the knowledge base first — if no match is found, it generates a fresh explanation and saves it for everyone.
          </p>
        </div>

        {/* Input Panel */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
          {/* Language Selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-zinc-500 font-medium">Language:</span>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => setLanguage(lang.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    language === lang.value
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' :'border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea */}
          <div className="relative">
            <textarea
              value={errorMessage}
              onChange={(e) => setErrorMessage(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAnalyze();
              }}
              placeholder={`Paste your error message here…\n\nExamples:\n• TypeError: Cannot read properties of undefined\n• IndexError: list index out of range\n• Notice: Undefined index: user_id`}
              rows={8}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-3.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/20 resize-none transition-all"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            />
            {errorMessage && (
              <button
                onClick={() => { setErrorMessage(''); setResult(null); setError(null); }}
                className="absolute top-3 right-3 text-zinc-600 hover:text-zinc-400 transition-colors p-1 rounded"
                aria-label="Clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            )}
          </div>

          {/* Quick Examples */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-600">Try:</span>
            {EXAMPLE_ERRORS.map((ex) => (
              <button
                key={ex.label}
                onClick={() => handleExampleClick(ex)}
                className="text-xs px-2.5 py-1 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-all"
              >
                {ex.label}
              </button>
            ))}
          </div>

          {/* Analyze Button */}
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-zinc-600">
              <kbd className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-500">⌘ Enter</kbd> to analyze
            </p>
            <button
              onClick={handleAnalyze}
              disabled={!errorMessage.trim() || isAnalyzing}
              className="flex items-center gap-2.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-zinc-950 font-semibold text-sm px-6 py-2.5 rounded-xl transition-all duration-150 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing…
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  Analyze Error
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 text-sm text-red-300">
            <span className="font-semibold">Error: </span>{error}
          </div>
        )}

        {/* Loading Skeleton */}
        {isAnalyzing && (
          <div className="space-y-4 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-3">
                <div className="h-4 bg-zinc-800 rounded w-1/4" />
                <div className="h-3 bg-zinc-800 rounded w-full" />
                <div className="h-3 bg-zinc-800 rounded w-3/4" />
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        {result && !isAnalyzing && (
          <div ref={resultRef} className="space-y-4">
            {/* Match badge */}
            <div className="flex items-center gap-2">
              {result.matched ? (
                <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs px-3 py-1.5 rounded-full font-medium">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                  Found in knowledge base (similarity: {Math.round(result.similarity_score * 100)}%)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/30 text-violet-300 text-xs px-3 py-1.5 rounded-full font-medium">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" /></svg>
                  Generated by AI — saved to knowledge base
                </span>
              )}
            </div>

            {/* Explanation */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                </div>
                <h2 className="text-sm font-semibold text-white">Explanation</h2>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">{result.explanation}</p>
            </div>

            {/* Cause */}
            {result.cause && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                  </div>
                  <h2 className="text-sm font-semibold text-white">Root Cause</h2>
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">{result.cause}</p>
              </div>
            )}

            {/* Fix */}
            {result.fix && (
              <div className="bg-zinc-900 border border-emerald-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                  </div>
                  <h2 className="text-sm font-semibold text-white">How to Fix</h2>
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">{result.fix}</p>
              </div>
            )}

            {/* Example Code */}
            {result.example_code && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
                    </div>
                    <h2 className="text-sm font-semibold text-white">Example Solution</h2>
                  </div>
                  <button
                    onClick={handleCopyCode}
                    className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1 rounded-lg hover:bg-zinc-800"
                  >
                    {copiedCode ? (
                      <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>Copied!</>
                    ) : (
                      <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>Copy</>
                    )}
                  </button>
                </div>
                <pre className="p-5 text-xs text-zinc-300 overflow-x-auto leading-relaxed" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  <code>{result.example_code}</code>
                </pre>
              </div>
            )}

            {/* Related Errors */}
            {result.related_errors && result.related_errors.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-white mb-4">Related Errors</h2>
                <div className="space-y-2">
                  {result.related_errors.map((rel) => (
                    <Link
                      key={rel.id}
                      href={`/errors/${rel.slug}`}
                      className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-zinc-800/60 transition-colors group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/60 flex-shrink-0" />
                      <span className="flex-1 text-xs text-zinc-300 group-hover:text-cyan-400 transition-colors font-mono truncate">
                        {rel.error_message.slice(0, 80)}
                      </span>
                      <span className="text-xs text-zinc-600 capitalize flex-shrink-0">{rel.language}</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-600 group-hover:text-cyan-400 transition-colors flex-shrink-0"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Try Again */}
            <div className="flex justify-center pt-2">
              <button
                onClick={() => { setResult(null); setErrorMessage(''); setError(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors px-4 py-2 rounded-lg hover:bg-zinc-800/60"
              >
                ← Debug another error
              </button>
            </div>
          </div>
        )}

        {/* API Info Banner */}
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white mb-1">Developer API Available</h3>
              <p className="text-xs text-zinc-400 mb-3">
                Integrate this debugging engine into your IDE, CI/CD pipeline, or AI system. Send a POST request to <code className="text-cyan-400 bg-zinc-800 px-1.5 py-0.5 rounded text-xs">/api/debug</code> with your error message.
              </p>
              <div className="bg-zinc-950 rounded-xl p-3 text-xs font-mono text-zinc-400 overflow-x-auto">
                <span className="text-violet-400">POST</span> /api/debug<br />
                <span className="text-zinc-600">{'{'}</span> <span className="text-amber-300">"error"</span>: <span className="text-emerald-300">"TypeError: undefined is not a function"</span>, <span className="text-amber-300">"language"</span>: <span className="text-emerald-300">"javascript"</span> <span className="text-zinc-600">{'}'}</span>
              </div>
              <a
                href="/api/debug"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                View full API documentation →
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
